// src/pages/Rugby.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API_BASE } from "../api";

/* ── colour palette ────────────────────────────────────────── */
const C = {
  pageBg:     "#f0fdf4",          // fresh green wash
  cardBg:     "#ffffff",
  pitchLine:  "#16a34a",          // green pitch lines
  pitchStripe:"#22c55e",          // lighter green for stripes
  tryZone:    "#15803d",          // darker green for try zones
  accentDark: "#166534",          // dark green – headings
  accentLight:"#bbf7d0",          // light green pill / badge bg
  textPri:    "#1e293b",
  textSub:    "#475569",
  textMut:    "#94a3b8",
};

/* ── SVG: rugby pitch background (fixed) ──── */
function PitchBg() {
  return (
    <svg
      viewBox="0 0 800 400"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "fixed", inset: 0, width: "100%", height: "100%",
        opacity: 0.12, pointerEvents: "none", zIndex: 0,
      }}
    >
      {/* Pitch outline */}
      <rect x="40" y="60" width="720" height="280"
            fill="none" stroke={C.pitchLine} strokeWidth="3" />

      {/* Try zones */}
      <rect x="40" y="60" width="60" height="280"
            fill={C.tryZone} opacity="0.15" />
      <rect x="700" y="60" width="60" height="280"
            fill={C.tryZone} opacity="0.15" />

      {/* Try lines */}
      <line x1="100" y1="60" x2="100" y2="340"
            stroke={C.pitchLine} strokeWidth="3" />
      <line x1="700" y1="60" x2="700" y2="340"
            stroke={C.pitchLine} strokeWidth="3" />

      {/* Halfway line */}
      <line x1="400" y1="60" x2="400" y2="340"
            stroke={C.pitchLine} strokeWidth="2" />

      {/* 22m lines */}
      <line x1="180" y1="60" x2="180" y2="340"
            stroke={C.pitchStripe} strokeWidth="1.5" strokeDasharray="8,4" />
      <line x1="620" y1="60" x2="620" y2="340"
            stroke={C.pitchStripe} strokeWidth="1.5" strokeDasharray="8,4" />

      {/* 10m lines */}
      <line x1="280" y1="60" x2="280" y2="340"
            stroke={C.pitchStripe} strokeWidth="1" strokeDasharray="4,2" />
      <line x1="520" y1="60" x2="520" y2="340"
            stroke={C.pitchStripe} strokeWidth="1" strokeDasharray="4,2" />

      {/* Centre circle */}
      <circle cx="400" cy="200" r="40"
              fill="none" stroke={C.pitchLine} strokeWidth="1.5" />
    </svg>
  );
}

// Premium rugby competitions to display
const PREMIUM_LEAGUES = [
  "SIX NATIONS",
  "PREMIERSHIP",
  "UNITED RUGBY CHAMPIONSHIP",
  "URC",
  "EUROPEAN RUGBY CHAMPIONS CUP",
  "ERCC",
  "TOP 14",
  "RUGBY WORLD CUP",
];

export default function Rugby() {
  const [games, setGames] = useState([]);
  const [daysAhead, setDaysAhead] = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const fetchGames = async () => {
    setLoading(true);
    setError("");
    try {
      const all = [];
      const today = new Date();
      for (let i = 0; i < daysAhead; i++) {
        const d = new Date(today);
        d.setDate(d.getDate() + i);
        const day = d.toISOString().slice(0, 10);
        const url = `${API_BASE}/api/public/fixtures/daily?day=${day}&sport=rugby`;
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) continue;
        const data = await r.json();
        if (data.fixtures) {
          // Filter to only show premium leagues
          const premiumGames = data.fixtures.filter(g => {
            const comp = (g.comp || "").toUpperCase();
            return PREMIUM_LEAGUES.some(league => comp.includes(league));
          });
          all.push(...premiumGames);
        }
      }
      setGames(all);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [daysAhead]);

  const formatTimeUTC = (isoString) => {
    if (!isoString) return "—";
    const safe = /Z$|[+-]\d{2}:\d{2}$/.test(isoString) ? isoString : isoString + "Z";
    try {
      const d = new Date(safe);
      return d.toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC",
      });
    } catch {
      return isoString.slice(11, 16);
    }
  };

  return (
    <div style={{ position: "relative", minHeight: "100vh", background: C.pageBg }}>
      <PitchBg />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", padding: 24 }}>
        <h2 style={{ color: C.accentDark, marginBottom: 20 }}>🏉 Rugby — Upcoming Matches</h2>

        <div
          style={{
            background: C.cardBg,
            borderRadius: 12,
            padding: 20,
            marginBottom: 20,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: C.textSub }}>Show next</span>
          <input
            type="number"
            min={1}
            max={30}
            value={daysAhead}
            onChange={(e) => setDaysAhead(parseInt(e.target.value || "7", 10))}
            style={{
              width: 60,
              padding: "6px 8px",
              border: `1px solid ${C.accentLight}`,
              borderRadius: 6,
              fontSize: 14,
            }}
          />
          <span style={{ color: C.textSub }}>days</span>
          <button
            onClick={fetchGames}
            style={{
              background: C.accentDark,
              color: "#fff",
              border: "none",
              padding: "6px 14px",
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Refresh
          </button>
        </div>

        {/* Odds coming soon banner */}
        <div
          style={{
            background: C.accentLight,
            border: `2px solid ${C.pitchLine}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            textAlign: "center",
          }}
        >
          <p style={{ color: C.accentDark, fontWeight: 600, margin: 0 }}>
            📊 Odds coming soon! Rugby API subscription will be activated shortly.
          </p>
        </div>

        {error && <p style={{ color: "red", marginBottom: 16 }}>{error}</p>}

        {loading ? (
          <p style={{ color: C.textSub }}>Loading...</p>
        ) : games.length === 0 ? (
          <p style={{ color: C.textMut }}>No rugby matches found in the selected window.</p>
        ) : (
          <div
            style={{
              background: C.cardBg,
              borderRadius: 12,
              padding: 20,
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${C.pitchLine}` }}>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Time (UTC)
                  </th>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Matchup
                  </th>
                  <th align="left" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Competition
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Best Home
                  </th>
                  <th align="right" style={{ padding: "10px 0", color: C.textSub, fontWeight: 600 }}>
                    Best Away
                  </th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => (
                  <tr
                    key={g.id}
                    style={{
                      cursor: "pointer",
                      borderBottom: `1px solid ${C.accentLight}`,
                      transition: "background 0.15s",
                    }}
                    onClick={() => navigate(`/rugby/match/${g.id}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = C.accentLight + "40")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "12px 0", color: C.textPri }}>{formatTimeUTC(g.kickoff_utc)}</td>
                    <td style={{ padding: "12px 0" }}>
                      <Link
                        to={`/rugby/match/${g.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, textDecoration: "none", color: "inherit" }}
                      >
                        {g.home_logo && (
                          <img
                            src={g.home_logo}
                            alt={g.home_team}
                            width={22}
                            height={22}
                            style={{ objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <strong style={{ color: C.textPri }}>{g.home_team}</strong>
                        <span style={{ opacity: 0.5, color: C.textMut }}>vs</span>
                        {g.away_logo && (
                          <img
                            src={g.away_logo}
                            alt={g.away_team}
                            width={22}
                            height={22}
                            style={{ objectFit: "contain" }}
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                        <span style={{ color: C.textPri }}>{g.away_team}</span>
                      </Link>
                    </td>
                    <td style={{ padding: "12px 0", color: C.textSub }}>{g.comp || "RUGBY"}</td>
                    <td align="right" style={{ padding: "12px 0", color: C.textPri }}>
                      {g.best_home ? `${g.best_home.price} (${g.best_home.bookmaker})` : "—"}
                    </td>
                    <td align="right" style={{ padding: "12px 0", color: C.textPri }}>
                      {g.best_away ? `${g.best_away.price} (${g.best_away.bookmaker})` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
